import { useMutation } from "@tanstack/react-query";
import { Check, ChefHat, Printer, Undo2 } from "lucide-react";
import { useState } from "react";

import { Button, Dialog } from "@/components/ui";
import { formatMoney } from "@/lib/format";
import type { Sale } from "@/repositories/salesRepository";
import { useAuthStore } from "@/stores/authStore";

import { browserPrinter } from "@/peripherals";
import { ordersRepository } from "@/repositories/ordersRepository";

import { RefundDialog } from "./RefundDialog";

/**
 * The receipt, on screen.
 *
 * Printed from the sale's own **snapshot** — the line descriptions and prices
 * stored at the moment of sale — never from the current catalogue. That is the
 * whole reason those columns are duplicated onto the line: this dialog must
 * render the same thing in 2031, after the product has been renamed, repriced
 * and archived.
 */
export function ReceiptDialog({
  sale,
  onClose,
  sectionId,
  tillId,
}: {
  sale: Sale & { queued?: boolean };
  onClose: () => void;
  /** Where the food gets made. Without it there is nothing to send an order to. */
  sectionId?: string | undefined;
  /** This till. A refund's money comes out of its drawer, whichever till took it. */
  tillId?: string | undefined;
}) {
  const change = Number(sale.change_given);
  const can = useAuthStore((s) => s.can);
  const [refunding, setRefunding] = useState(false);
  const [refunded, setRefunded] = useState<Sale | null>(null);

  /**
   * Send it to the kitchen.
   *
   * The money has already been taken — pay-then-wait is how a township takeaway
   * works — so this creates the order **against the sale that just completed**,
   * which is what marks it paid on the kitchen screen. Somebody handing over a
   * bag needs to know that before they let go of it.
   *
   * Offered rather than automatic: most sales at a spaza till are a cooldrink and
   * a loaf, and a kitchen queue filling up with those would be a queue nobody
   * reads.
   */
  const sendToKitchen = useMutation({
    mutationFn: () =>
      ordersRepository.place({
        section_id: sectionId!,
        sale_id: sale.id,
        lines: sale.lines.map((line) => ({
          product_id: line.product,
          quantity: line.quantity,
          // The extras are already on the sale line's snapshot; the order takes
          // its own copy from the same ids so the ticket says what was chosen.
          modifier_ids: (line.modifiers ?? []).map((modifier) => modifier.id),
        })),
      }),
  });

  /**
   * What a driver would need, assembled once.
   *
   * The browser printer ignores it — it prints the DOM — but building it here
   * keeps the receipt's *content* in one place, so a real ESC/POS driver added
   * later formats from data rather than scraping the page.
   */
  const receiptPayload = {
    receiptNumber: sale.receipt_number,
    merchantName: "",
    lines: sale.lines.map((line) => ({
      description: line.description,
      quantity: line.quantity,
      unitPrice: line.unit_price,
      lineTotal: line.line_total,
    })),
    subtotal: sale.subtotal,
    taxTotal: sale.tax_total,
    total: sale.total,
    tendered: sale.amount_paid,
    change: sale.change_given,
    method: sale.payments[0]?.method ?? "CASH",
    cashier: sale.cashier_name,
    when: sale.completed_at ?? sale.created_at,
    queued: sale.queued ?? false,
  };

  // Only for a sale the server has, and only when there is a counter to send it
  // to. A queued sale has no server-side id for an order to point at.
  const maySend =
    can("sales.create") && !sale.queued && sectionId != null && !sendToKitchen.isSuccess;

  // Only for a sale the server has. A refund reverses a transaction by id, and a
  // sale still sitting in the outbox has no server-side row to reverse — so the
  // button would fail in the one situation a cashier least wants a failure.
  // A part-refunded sale can be refunded again: somebody brings back the
  // second of two faulty kettles on Thursday.
  // A cashier who may not refund on their own still gets the button: a
  // manager approves with their code and PIN in the refund dialog.
  const mayRefund =
    (can("sales.refund") || can("sales.create")) &&
    !sale.queued &&
    (sale.status === "COMPLETED" || sale.status === "PARTLY_REFUNDED") &&
    !refunded;

  if (refunding) {
    return (
      <RefundDialog
        sale={sale}
        tillId={tillId}
        needsManager={!can("sales.refund")}
        onClose={() => setRefunding(false)}
        onDone={(refund) => {
          setRefunded(refund);
          setRefunding(false);
        }}
      />
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title={sale.duplicate ? "Already completed" : "Sale complete"}
      description={sale.receipt_number}
      footer={
        <>
          <Button variant="secondary" onClick={() => void browserPrinter.print(receiptPayload)}>
            <Printer className="size-4" aria-hidden /> Print
          </Button>
          {mayRefund && (
            <Button variant="secondary" onClick={() => setRefunding(true)}>
              <Undo2 className="size-4" aria-hidden /> Refund
            </Button>
          )}
          {maySend && (
            <Button
              variant="secondary"
              loading={sendToKitchen.isPending}
              onClick={() => sendToKitchen.mutate()}
            >
              <ChefHat className="size-4" aria-hidden /> To kitchen
            </Button>
          )}
          <Button variant="primary" onClick={onClose}>
            Next customer
          </Button>
        </>
      }
    >
      {/* The print stylesheet hides everything on the page except this element.
          Before it existed, Print sent the whole page — sidebar, product grid
          and all — to whatever printer the OS had. */}
      <div className="space-y-4" data-print="receipt">
        {sendToKitchen.isSuccess && (
          <p className="rounded-card border border-brand-500/30 bg-brand-50 px-4 py-3 text-sm text-ink-900">
            Order #{sendToKitchen.data.number} is on the kitchen screen.
          </p>
        )}

        {refunded && (
          <p className="rounded-card border border-brand-500/30 bg-brand-50 px-4 py-3 text-sm text-ink-900">
            {formatMoney(String(Math.abs(Number(refunded.total))))} refunded on{" "}
            {refunded.receipt_number}.
          </p>
        )}

        {sale.queued && (
          /* Said plainly rather than hidden. The sale is complete as far as the
             customer is concerned — they have paid and have the receipt — and
             the shopkeeper deserves to know it has not reached the server yet,
             because that is the number their end-of-day report depends on. */
          <p className="rounded-card border border-warn-500/30 bg-warn-50 px-4 py-3 text-sm text-warn-700">
            Saved on this till and waiting to upload. The customer is served; it will send itself
            when the connection returns.
          </p>
        )}

        {sale.duplicate && (
          <p className="rounded-card border border-info-500/30 bg-info-50 px-4 py-3 text-sm text-info-500">
            This sale had already gone through — this is the same receipt, not a second sale. The
            customer has been charged once.
          </p>
        )}

        {change > 0 && (
          <div className="rounded-card bg-brand-50 px-4 py-3 text-center">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">Change</p>
            <p className="text-total tabular-nums text-brand-600">
              {formatMoney(sale.change_given)}
            </p>
          </div>
        )}

        <ul className="divide-y divide-line text-sm">
          {sale.lines.map((line) => (
            <li key={line.id} className="flex items-baseline justify-between gap-3 py-2">
              <span className="min-w-0">
                <span className="tabular-nums text-ink-500">{Number(line.quantity)}x </span>
                <span className="text-ink-900">{line.description}</span>
              </span>
              <span className="tabular-nums text-ink-900">{formatMoney(line.line_total)}</span>
            </li>
          ))}
        </ul>

        <div className="space-y-1 border-t border-line pt-3 text-sm">
          {Number(sale.tax_total) > 0 && (
            <div className="flex justify-between text-ink-600">
              <span>VAT ({Number(sale.vat_rate)}%)</span>
              <span className="tabular-nums">{formatMoney(sale.tax_total)}</span>
            </div>
          )}
          {Number(sale.rounding_adjustment) !== 0 && (
            <div className="flex justify-between text-ink-600">
              {/* Named rather than folded into the total: the shopkeeper has to
                  be able to explain every cent at cash-up. */}
              <span>Cash rounding</span>
              <span className="tabular-nums">{formatMoney(sale.rounding_adjustment)}</span>
            </div>
          )}
          <div className="flex justify-between pt-1 text-base font-semibold text-ink-900">
            <span>Total</span>
            <span className="tabular-nums">{formatMoney(sale.total)}</span>
          </div>
        </div>

        <p className="flex items-center gap-2 text-xs text-ink-500">
          <Check className="size-3.5 text-brand-600" aria-hidden />
          {sale.cashier_name} · {sale.till_name}
        </p>
      </div>
    </Dialog>
  );
}
